import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { Link, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaTimes,
  FaEye,
  FaEraser,
  FaChevronLeft,
  FaChevronRight,
  FaSearch,
  FaEdit,
  FaTrash,
  FaBarcode,
  FaFileInvoice,
} from 'react-icons/fa';

import Loading from '~/components/Loading';

import { useAuth } from '~/hooks';

import api from '~/services/api';

import { Select } from '~/components/Form';
import { TableLoading } from '~/components/Table';
import Pagination from '~/components/Pagination';

import {
  Container,
  Header,
  Controls,
  Table,
  DetailsContainer,
} from '~/styles/components';
import ConfirmWindow from '~/components/ConfirmWindow';
import { confirmAlert } from 'react-confirm-alert';
import formatValue from '~/util/formatValue';
import { formatValueFloat } from '~/util/formatValueFloat';
import { FaPlus, FaSort, FaSortDown, FaSortUp } from 'react-icons/fa6';
import { Filter, Content, BilletInfo } from './styles';
import GenerateBilletsModal from './GenerateBilletsModal';

const List = () => {
  const { company, companyUser } = useAuth();
  const history = useHistory();

  const filterRef = useRef(null);

  const [loading, setLoading] = useState(true);

  const [showTable, setShowTable] = useState(true);

  const [monthlyPayments, setMonthlyPayments] = useState([]);
  const [monthlyPayment, setMonthlyPayment] = useState({});
  const [monthlyPaymentIndex, setMonthlyPaymentIndex] = useState(0);
  const [totalMonthlyPayments, setTotalMonthlyPayments] = useState(0);

  const [currentPage, setCurrentPage] = useState(() => {
    const currentPageStorage = localStorage.getItem(
      '@Diretiva:monthly-payments:page'
    );

    if (currentPageStorage) {
      return Number(currentPageStorage);
    }

    return 1;
  });
  const [totalPages, setTotalPages] = useState(1);

  const [selectedClient, setSelectedClient] = useState(() => {
    const clientStorage = localStorage.getItem(
      '@Diretiva:monthly-payments:filter:drawee'
    );

    if (clientStorage) {
      return JSON.parse(clientStorage);
    }

    return { value: '', label: 'Todos' };
  });

  const [selectedStatus, setSelectedStatus] = useState(() => {
    const statusStorage = localStorage.getItem(
      '@Diretiva:monthly-payments:filter:status'
    );

    if (statusStorage) {
      return JSON.parse(statusStorage);
    }

    return { value: '', label: 'Todos' };
  });

  // Limite de empregados: inteiro inicializado pelo backend (company.employee_limit) ou 0
  // Não pode ser menor que zero — garantimos ao inicializar e ao alterar pelo input
  const [employeeLimit, setEmployeeLimit] = useState(() =>
    typeof company?.employee_limit === 'number'
      ? Math.max(0, company.employee_limit)
      : 0
  );

  useEffect(() => {
    setEmployeeLimit(
      typeof company?.employee_limit === 'number'
        ? Math.max(0, company.employee_limit)
        : 0
    );
  }, [company]);

  // Quando um item é selecionado nos detalhes, mostrar o employee_limit do item
  useEffect(() => {
    if (
      monthlyPayment &&
      monthlyPayment.employee_limit !== undefined &&
      monthlyPayment.employee_limit !== null
    ) {
      const val = Number(monthlyPayment.employee_limit);
      setEmployeeLimit(Number.isNaN(val) ? 0 : Math.max(0, val));
    } else {
      setEmployeeLimit(
        typeof company?.employee_limit === 'number'
          ? Math.max(0, company.employee_limit)
          : 0
      );
    }
  }, [monthlyPayment, company]);

  const [sortConfig, setSortConfig] = useState({
    key: 'drawee',
    direction: 'asc',
  });

  const [showGenerateBilletsModal, setShowGenerateBilletsModal] = useState(
    false
  );

  const [
    showGenerateSingleBilletModal,
    setShowGenerateSingleBilletModal,
  ] = useState(false);
  // IDs selecionados para gerar cobranças em massa
  const [selectedMonthlyPaymentIds, setSelectedMonthlyPaymentIds] = useState(
    []
  );

  const sort = useCallback(
    key => {
      let direction = 'asc';
      if (
        sortConfig &&
        sortConfig.key === key &&
        sortConfig.direction === 'asc'
      ) {
        direction = 'desc';
      }
      setSortConfig({ key, direction });
    },
    [sortConfig]
  );

  const loadMonthlyPayments = useCallback(async () => {
    if (company) {
      try {
        setShowTable(true);
        setLoading(true);
        const response = await api.get('/monthly-payments', {
          params: {
            company_id: company.old_id,
            page: currentPage,
            key: sortConfig.key,
            direction: sortConfig.direction,
            drawee: selectedClient.value,
            status: selectedStatus.value,
          },
        });

        if (response.data.docs.length > 0) {
          const formattedMonthlyPayments = response.data.docs.map(item => ({
            ...item,
            price: formatValue(item.price),
            interest: formatValueFloat(item.interest),
            fine: formatValueFloat(item.fine),
            monthly_payment_finance: item.monthly_payment_finance.map(
              finance => ({
                ...finance,
                price: formatValue(finance.price),
              })
            ),
            // Garantir que species tenha um valor válido
            species: [2, 4].includes(item.species) ? item.species : 4,
          }));

          setMonthlyPayments(formattedMonthlyPayments);
          // limpar seleção ao recarregar a nova lista
          setSelectedMonthlyPaymentIds([]);
          setTotalPages(response.data.pages);
          setTotalMonthlyPayments(response.data.total);
          setMonthlyPayment(formattedMonthlyPayments[0]);
        } else {
          toast.warn('Nenhuma mensalidade foi encontrada.', {
            position: toast.POSITION.BOTTOM_RIGHT,
          });
        }
      } catch (error) {
        toast.error(
          `Falha ao buscar mensalidades: ${JSON.stringify(
            error.response?.data?.error
          ) ||
            error.message ||
            'Erro desconhecido'}`,
          {
            position: toast.POSITION.BOTTOM_RIGHT,
          }
        );
      } finally {
        setLoading(false);
      }
    }
  }, [company, selectedClient, selectedStatus, sortConfig, currentPage]);
  const toggleSelect = useCallback((e, id) => {
    // evitar que o clique no checkbox dispare o onClick da linha
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    setSelectedMonthlyPaymentIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  }, []);

  const toggleSelectAll = useCallback(
    e => {
      if (e && e.stopPropagation) {
        e.stopPropagation();
      }
      if (selectedMonthlyPaymentIds.length === monthlyPayments.length) {
        setSelectedMonthlyPaymentIds([]);
      } else {
        setSelectedMonthlyPaymentIds(monthlyPayments.map(m => m.id));
      }
    },
    [monthlyPayments, selectedMonthlyPaymentIds.length]
  );

  const [clientsOptions, setClientsOptions] = useState([]);

  const loadClients = useCallback(async () => {
    if (company) {
      try {
        const response = await api.get(`/relationships`, {
          params: {
            company_id: company.id,
            selectOnly: true,
            active: true,
            type: 1,
          },
        });

        let options = [];
        if (response.data.length > 0) {
          response.data.sort((a, b) => {
            if (a.name.toUpperCase() < b.name.toUpperCase()) return -1;
            if (a.name.toUpperCase() > b.name.toUpperCase()) return 1;
            return 0;
          });

          options = response.data
            .map(item => ({
              value: item.old_id,
              label: item.name,
            }))
            .filter(
              option => option.value !== undefined && option.value !== null
            );
        } else {
          toast.warn('Nenhum cliente foi encontrado.', {
            position: toast.POSITION.BOTTOM_RIGHT,
          });
        }

        options.unshift({ value: '', label: 'Todos' });
        setClientsOptions(options);
      } catch (error) {
        toast.error(
          `Falha ao buscar clientes: ${JSON.stringify(
            error.response?.data?.error
          ) ||
            error.message ||
            'Erro desconhecido'}`,
          {
            position: toast.POSITION.BOTTOM_RIGHT,
          }
        );
      }
    }
  }, [company]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  const statusOptions = useMemo(() => {
    return [
      { value: '', label: 'Todos' },
      { value: true, label: 'Ativo' },
      { value: false, label: 'Inativo' },
    ];
  }, []);

  // const speciesOptions = useMemo(() => {
  //   return [
  //     { value: 4, label: 'Duplicata de serviço' },
  //     { value: 2, label: 'Fatura' },
  //   ];
  // }, []);

  const getSpeciesLabel = useCallback(speciesValue => {
    switch (speciesValue) {
      case 2:
        return 'Fatura';
      case 4:
        return 'Duplicata de serviço';
      default:
        return 'Espécie desconhecida';
    }
  }, []);

  useEffect(() => {
    loadMonthlyPayments();
  }, [loadMonthlyPayments]);

  const handleEdit = useCallback(() => {
    if (companyUser.level > 0)
      history.push(`/monthly-payments/edit/${monthlyPayment.id}`);
  }, [history, monthlyPayment, companyUser]);

  const handleDelete = useCallback(async () => {
    if (companyUser.level > 0) {
      try {
        await api.delete(`/monthly-payments/${monthlyPayment.id}`);
        toast.success('Mensalidade excluída com sucesso.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        loadMonthlyPayments();
      } catch {
        toast.error('Falha ao excluir mensalidade.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      }
    }
  }, [companyUser, monthlyPayment, loadMonthlyPayments]);

  const confirmDeleteForm = useCallback(() => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <ConfirmWindow
            onClick={handleDelete}
            onClose={onClose}
            message="Todos os dados serão Deletados com esta ação."
          />
        );
      },
      closeOnEscape: false,
      closeOnClickOutside: false,
    });
  }, [handleDelete]);

  const handleView = useCallback(() => {
    if (monthlyPayments.length > 0) {
      setShowTable(!showTable);
    }
  }, [showTable, monthlyPayments]);

  const handlePrevItem = useCallback(() => {
    if (monthlyPaymentIndex !== 0) {
      setMonthlyPaymentIndex(monthlyPaymentIndex - 1);
      setMonthlyPayment(monthlyPayments[monthlyPaymentIndex - 1]);
    } else {
      setMonthlyPaymentIndex(monthlyPayments.length - 1);
      setMonthlyPayment(monthlyPayments[monthlyPayments.length - 1]);
    }
  }, [monthlyPaymentIndex, monthlyPayments]);

  const handleNextItem = useCallback(() => {
    if (monthlyPaymentIndex !== monthlyPayments.length - 1) {
      setMonthlyPaymentIndex(monthlyPaymentIndex + 1);
      setMonthlyPayment(monthlyPayments[monthlyPaymentIndex + 1]);
    } else {
      setMonthlyPaymentIndex(0);
      setMonthlyPayment(monthlyPayments[0]);
    }
  }, [monthlyPaymentIndex, monthlyPayments]);

  const handleFilter = useCallback(
    ({ drawee, status }) => {
      setCurrentPage(1);
      localStorage.setItem('@Diretiva:monthly-payments:page', 1);

      setSelectedStatus(statusOptions.find(option => option.value === status));
      localStorage.setItem(
        '@Diretiva:monthly-payments:filter:status',
        JSON.stringify(statusOptions.find(option => option.value === status))
      );

      setSelectedClient(clientsOptions.find(option => option.value === drawee));
      localStorage.setItem(
        '@Diretiva:monthly-payments:filter:drawee',
        JSON.stringify(clientsOptions.find(option => option.value === drawee))
      );
    },
    [clientsOptions, statusOptions]
  );

  const handleResetFilter = useCallback(() => {
    setCurrentPage(1);
    localStorage.removeItem('@Diretiva:monthly-payments:page');

    filterRef.current.reset();

    filterRef.current.setFieldValue('status', {
      value: '',
      label: 'Todos',
    });
    setSelectedStatus({
      value: '',
      label: 'Todos',
    });
    localStorage.removeItem('@Diretiva:monthly-payments:filter:status');

    filterRef.current.setFieldValue('drawee', {
      value: '',
      label: 'Todos',
    });
    setSelectedClient({
      value: '',
      label: 'Todos',
    });
    localStorage.removeItem('@Diretiva:monthly-payments:filter:drawee');
  }, []);

  const handleDetails = useCallback(
    index => {
      setMonthlyPayment(monthlyPayments[index]);
      setMonthlyPaymentIndex(index);
      handleView();
    },
    [monthlyPayments, handleView]
  );

  const handlePage = useCallback(
    page => {
      if (page === 0) {
        setCurrentPage(1);
        localStorage.setItem('@Diretiva:monthly-payments:page', 1);
      } else if (page > totalPages) {
        setCurrentPage(totalPages);
        localStorage.setItem('@Diretiva:monthly-payments:page', totalPages);
      } else {
        setCurrentPage(page);
        localStorage.setItem('@Diretiva:monthly-payments:page', page);
      }
    },
    [totalPages]
  );

  const handleOpenGenerateBilletsModal = useCallback(() => {
    if (companyUser.level > 0) {
      setShowGenerateBilletsModal(true);
    }
  }, [companyUser]);

  const handleGenerateBilletsSuccess = useCallback(() => {
    // Recarregar a lista após gerar as cobranças
    loadMonthlyPayments();
  }, [loadMonthlyPayments]);

  const handleOpenGenerateSingleBilletModal = useCallback(() => {
    if (companyUser.level > 0) {
      setShowGenerateSingleBilletModal(true);
    }
  }, [companyUser]);

  const handleGenerateSingleBilletSuccess = useCallback(() => {
    // Recarregar a lista após gerar a cobrança
    loadMonthlyPayments();
  }, [loadMonthlyPayments]);

  const getExpirationTypeLabel = type => {
    switch (type) {
      case 1:
        return 'Fixo';
      case 2:
        return 'Variável';
      default:
        return '';
    }
  };

  const getInterestTypeLabel = type => {
    switch (type) {
      case 1:
        return '(R$) Valor por dia';
      case 2:
        return '(%) ao mês';
      default:
        return '';
    }
  };

  const getFineTypeLabel = type => {
    switch (type) {
      case 0:
        return 'Não';
      case 1:
        return 'Valor fixo';
      case 2:
        return 'Percentual';
      default:
        return '';
    }
  };

  const getProtestLabel = protest => {
    switch (protest) {
      case 1:
        return 'Sim';
      case 2:
        return 'Sim';
      case 3:
        return 'Não';
      default:
        return '';
    }
  };

  const getHowToChargeLabel = howToCharge => {
    switch (howToCharge) {
      case 0:
        return 'Email';
      case 1:
        return 'SMS';
      case 2:
        return 'Correio';
      default:
        return '';
    }
  };

  return (
    <>
      <Container>
        <Header>
          <div>
            <FaBarcode />
            <h1>Cobrança - Mensalidades</h1>
          </div>
          <Link to="/">
            <FaTimes />
          </Link>
        </Header>
        <Controls>
          <button type="button" onClick={handleView}>
            <FaEye />
            <span>Visualização</span>
          </button>
          <Link to="/monthly-payments/new">
            <FaPlus />
            <span>Nova</span>
          </Link>
          {showTable ? (
            <>
              <button type="button" onClick={handleResetFilter}>
                <FaEraser />
                <span>Limpar filtros</span>
              </button>
              <button type="button" onClick={handleOpenGenerateBilletsModal}>
                <FaFileInvoice />
                <span>Gerar Todas as Cobranças</span>
              </button>
            </>
          ) : (
            <div>
              <button type="button" onClick={handleEdit}>
                <FaEdit size={15} color="#44546a" />
                <span>Editar</span>
              </button>
              <button type="button" onClick={confirmDeleteForm}>
                <FaTrash size={15} color="#44546a" />
                <span>Excluir</span>
              </button>
              <button
                type="button"
                onClick={handleOpenGenerateSingleBilletModal}
              >
                <FaFileInvoice size={15} color="#44546a" />
                <span>Gerar Cobrança</span>
              </button>

              <button type="button" onClick={handlePrevItem}>
                <FaChevronLeft />
              </button>
              {totalMonthlyPayments > 25 ? (
                <span>{monthlyPaymentIndex + 1} de 25</span>
              ) : (
                <span>
                  {monthlyPaymentIndex + 1} de {totalMonthlyPayments}
                </span>
              )}
              <button type="button" onClick={handleNextItem}>
                <FaChevronRight />
              </button>
            </div>
          )}
        </Controls>
        {showTable && (
          <Filter ref={filterRef} onSubmit={handleFilter}>
            <Select
              label="Cliente"
              name="drawee"
              className="drawee"
              options={clientsOptions}
              defaultValue={selectedClient}
            />
            <Select
              label="Status"
              name="status"
              className="status"
              options={statusOptions}
              defaultValue={selectedStatus}
            />

            <button type="submit">
              <FaSearch />
            </button>
          </Filter>
        )}
        {loading ? (
          <TableLoading />
        ) : (
          <Content className="content">
            {showTable ? (
              <Table className="table">
                <thead>
                  <tr>
                    <th style={{ width: '4%' }}>
                      <input
                        type="checkbox"
                        aria-label="Selecionar todos"
                        onChange={toggleSelectAll}
                        checked={
                          monthlyPayments.length > 0 &&
                          selectedMonthlyPaymentIds.length ===
                            monthlyPayments.length
                        }
                      />
                    </th>
                    <th className="expiration_day sort">
                      <div>Dia Vencimento</div>
                    </th>
                    <th className="financial_data">Dados Financeiros</th>
                    <th className="drawee sort" onClick={() => sort('drawee')}>
                      <div>
                        Cliente (Sacado)
                        {sortConfig.key !== 'drawee' && <FaSort />}
                        {sortConfig.key === 'drawee' &&
                          sortConfig.direction === 'asc' && <FaSortUp />}
                        {sortConfig.key === 'drawee' &&
                          sortConfig.direction === 'desc' && <FaSortDown />}
                      </div>
                    </th>
                    <th className="price_list">Preço</th>
                    <th className="situation">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {monthlyPayments?.map((item, index) => (
                    <tr
                      key={item.id}
                      className="hover"
                      onClick={() => handleDetails(index)}
                    >
                      <td>
                        <input
                          type="checkbox"
                          aria-label={`Selecionar mensalidade ${item.id}`}
                          onChange={e => toggleSelect(e, item.id)}
                          checked={selectedMonthlyPaymentIds.includes(item.id)}
                          onClick={e => e.stopPropagation()}
                        />
                      </td>
                      <td className="expiration_day">{item?.expiration_day}</td>
                      <td className="financial_data">
                        {item?.financial_data?.title}
                      </td>
                      <td className="drawee">{item?.client?.name}</td>
                      <td className="price_list">{item?.price}</td>
                      <td className="situation">
                        {item.active ? 'Ativo' : 'Inativo'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <DetailsContainer>
                <BilletInfo>
                  <h4>Mensalidade</h4>
                  <section>
                    <div className="drawee">
                      <label>Cliente (Sacado)</label>
                      <input
                        name="drawee"
                        value={monthlyPayment?.client?.name || ''}
                        readOnly
                      />
                    </div>
                    <div className="employee_limit">
                      <label>Limite de empregados</label>
                      <input
                        name="employee_limit"
                        type="number"
                        min={0}
                        step={1}
                        value={employeeLimit}
                        onChange={e => {
                          const parsed = parseInt(e.target.value || '0', 10);
                          setEmployeeLimit(
                            Number.isNaN(parsed) ? 0 : Math.max(0, parsed)
                          );
                        }}
                      />
                    </div>
                    <div className="price">
                      <label>Preço</label>
                      <input
                        name="price"
                        value={monthlyPayment?.price || ''}
                        readOnly
                      />
                    </div>
                    <div className="species">
                      <label>Espécie</label>
                      <input
                        name="species"
                        value={
                          getSpeciesLabel(monthlyPayment?.species) ||
                          'Duplicata de serviço'
                        }
                        readOnly
                      />
                    </div>
                    <div className="financial_data">
                      <label>Dados financeiros</label>
                      <input
                        name="financial_data"
                        value={monthlyPayment?.financial_data?.title || ''}
                        readOnly
                      />
                    </div>
                  </section>
                  <section>
                    <div className="emission_day">
                      <label>Dia de Emissão</label>
                      <input
                        name="emission_day"
                        value={monthlyPayment?.emition_day || ''}
                        readOnly
                      />
                    </div>
                    <div className="expiration_type">
                      <label>Tipo de vencimento</label>
                      <input
                        name="expiration_type"
                        value={
                          getExpirationTypeLabel(
                            monthlyPayment?.expiration_type
                          ) || ''
                        }
                        readOnly
                      />
                    </div>
                    <div className="expiration_day">
                      <label>Dia de Vencimento</label>
                      <input
                        name="expiration_day"
                        value={monthlyPayment?.expiration_day || ''}
                        readOnly
                      />
                    </div>
                  </section>
                  <section>
                    <div className="interest">
                      <label>Juros</label>
                      <input
                        name="interest"
                        value={monthlyPayment?.interest || ''}
                        readOnly
                      />
                    </div>
                    <div className="interest_type">
                      <label>Juros (Tipo)</label>
                      <input
                        name="interest_type"
                        value={
                          getInterestTypeLabel(monthlyPayment?.interest_type) ||
                          ''
                        }
                        readOnly
                      />
                    </div>
                    <div className="fine">
                      <label>Multa</label>
                      <input
                        name="fine"
                        value={monthlyPayment?.fine || ''}
                        readOnly
                      />
                    </div>
                    <div className="fine_type">
                      <label>Multa (Tipo)</label>
                      <input
                        name="fine_type"
                        value={
                          getFineTypeLabel(monthlyPayment?.fine_type) || ''
                        }
                        readOnly
                      />
                    </div>
                    <div className="days_to_fine">
                      <label>Dias para multar</label>
                      <input
                        name="days_to_fine"
                        value={monthlyPayment?.days_to_fine || ''}
                        readOnly
                      />
                    </div>
                    <div className="protest">
                      <label>Protestar</label>
                      <input
                        name="protest"
                        value={getProtestLabel(monthlyPayment?.protest) || ''}
                        readOnly
                      />
                    </div>
                    <div className="how_to_charge">
                      <label>Forma de Cobrança</label>
                      <input
                        name="how_to_charge"
                        value={
                          getHowToChargeLabel(monthlyPayment?.how_to_charge) ||
                          ''
                        }
                        readOnly
                      />
                    </div>
                  </section>
                  <section>
                    <div className="message">
                      <label>Mensagem</label>
                      <input
                        name="message"
                        value={monthlyPayment?.message || ''}
                        readOnly
                      />
                    </div>
                  </section>

                  <h4 className="finance_header">Registros Financeiros</h4>
                  {monthlyPayment.monthly_payment_finance?.length > 0 &&
                    monthlyPayment.monthly_payment_finance.map(finance => (
                      <section key={finance.id}>
                        <div className="finance_name_label">
                          <label>Conta</label>
                          <input
                            name="finance_name_label"
                            value={finance.event?.name_label || ''}
                            readOnly
                          />
                        </div>
                        <div className="finance_price">
                          <label>Valor</label>
                          <input
                            name="finance_price"
                            value={finance.price || ''}
                            readOnly
                          />
                        </div>
                      </section>
                    ))}
                </BilletInfo>
              </DetailsContainer>
            )}
          </Content>
        )}
        <Pagination
          loading={loading ? 1 : 0}
          currentPage={currentPage}
          pages={totalPages}
          totalDocs={totalMonthlyPayments}
          handlePage={handlePage}
        />
      </Container>
      {loading && <Loading />}
      <GenerateBilletsModal
        isOpen={showGenerateBilletsModal}
        setIsOpen={setShowGenerateBilletsModal}
        monthlyPaymentIds={selectedMonthlyPaymentIds}
        onSuccess={handleGenerateBilletsSuccess}
        isSingle={false}
      />
      <GenerateBilletsModal
        isOpen={showGenerateSingleBilletModal}
        setIsOpen={setShowGenerateSingleBilletModal}
        monthlyPaymentIds={monthlyPayment?.id ? [monthlyPayment.id] : []}
        onSuccess={handleGenerateSingleBilletSuccess}
        isSingle
      />
    </>
  );
};

export default List;
